import config from 'config/Config';
import BaseApi from 'api/normalized/BaseApi';
import {
  AgentDashboardShare,
  Dashboard,
  DashboardOwner,
  DashboardUserPermission,
} from 'utils/customTypes';

export interface AgentDashboardOwnerResponse {
  id: string;
  organizationId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  status: string;
  type: string;
}

export interface AgentDashboardResponse {
  id: string;
  name: string | null;
  owner: AgentDashboardOwnerResponse;
  conversationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgentDashboardsListResponse {
  dashboards: AgentDashboardResponse[];
}

export interface AgentDashboardShareResponse {
  id: string;
  email: string;
  createdAt: string;
}

export interface AgentDashboardSharesListResponse {
  shares: AgentDashboardShareResponse[];
}

export interface CreateAgentDashboardShareResponse {
  id: string;
  dashboardId: string;
  email: string;
  token: string;
  createdAt: string;
}

const mapOwner = (owner: AgentDashboardOwnerResponse): DashboardOwner => ({
  id: owner.id,
  externalId: '',
  organizationId: owner.organizationId,
  externalOrganizationId: '',
  status: owner.status,
  firstName: owner.firstName ?? '',
  lastName: owner.lastName ?? '',
  email: owner.email ?? '',
  disabled: owner.status === 'inactive',
});

const resolvePermission = (
  owners: DashboardOwner[],
  currentUserId?: string
): DashboardUserPermission => {
  const isOwner =
    !!currentUserId && owners.some((owner) => owner.id === currentUserId);
  return isOwner
    ? DashboardUserPermission.OWNER
    : DashboardUserPermission.VIEWER;
};

const mapDashboard = (
  dashboard: AgentDashboardResponse,
  currentUserId?: string
): Dashboard => {
  const owners = [mapOwner(dashboard.owner)];
  return {
    id: dashboard.id,
    name: dashboard.name ?? '',
    createdAt: dashboard.createdAt,
    updatedAt: dashboard.updatedAt,
    owners,
    permission: resolvePermission(owners, currentUserId),
    conversationId: dashboard.conversationId ?? undefined,
  };
};

export class AgentDashboardApi extends BaseApi<Dashboard> {
  constructor() {
    super(
      `${config.get('agentBackendURL')}reporting-agent/dashboards`,
      'agent'
    );
  }

  async getDashboards(currentUserId?: string): Promise<Dashboard[]> {
    const { data } = await this.axiosInstance.get<AgentDashboardsListResponse>(
      ''
    );
    return (data.dashboards ?? []).map((dashboard) =>
      mapDashboard(dashboard, currentUserId)
    );
  }

  async getShares(artifactId: string): Promise<AgentDashboardShare[]> {
    const { data } =
      await this.axiosInstance.get<AgentDashboardSharesListResponse>(
        `${artifactId}/shares`
      );
    return (data.shares ?? []).map((share) => ({
      id: share.id,
      email: share.email,
      createdAt: share.createdAt,
    }));
  }

  async createShare(
    artifactId: string,
    email: string
  ): Promise<AgentDashboardShare> {
    const { data } =
      await this.axiosInstance.post<CreateAgentDashboardShareResponse>(
        `${artifactId}/shares`,
        { email }
      );
    // The response also carries a one-time `token` used to build a share link;
    // it is intentionally dropped here since the list surface only needs the
    // recipient and when the share was created.
    return {
      id: data.id,
      email: data.email,
      createdAt: data.createdAt,
    };
  }

  async revokeShare(artifactId: string, shareId: string): Promise<void> {
    await this.axiosInstance.delete(`${artifactId}/shares/${shareId}`);
  }

  async setVisibility(dashboardId: string, visible: boolean): Promise<void> {
    await this.axiosInstance.patch(dashboardId, { visible });
  }
}

export default AgentDashboardApi;
